import React, { useMemo } from 'react';
import { Space, Button, Tag, Typography } from 'antd';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import EnhancedTableWrapper from '../../../components/EnhancedTableWrapper';
import { financeDefaultColumns } from './financeColumns.config';
import {
  FinanceRecordWithDetails,
  ExpenseType,
  PaymentNature,
  EntityType,
} from '../../../services/finance';

const { Text } = Typography;

// 费用类型映射
const EXPENSE_TYPE_MAP: Record<ExpenseType, string> = {
  [ExpenseType.CONSULTATION_FEE]: '咨询费',
  [ExpenseType.LAWYER_FEE]: '律师费',
  [ExpenseType.CONSULTANT_FEE]: '顾问费',
  [ExpenseType.SERVICE_FEE]: '服务费',
  [ExpenseType.TRAVEL_FEE]: '差旅费',
  [ExpenseType.OTHER]: '其他',
};

// 款项类型映射
const PAYMENT_NATURE_MAP: Record<PaymentNature, string> = {
  [PaymentNature.RECEIVED]: '已收款',
  [PaymentNature.RECEIVABLE]: '应收款',
  [PaymentNature.REFUND]: '退款',
};

// 实体类型映射
const ENTITY_TYPE_MAP: Record<EntityType, string> = {
  [EntityType.LEAD]: '线索',
  [EntityType.CASE]: '案件',
};

const getPaymentNatureColor = (nature: PaymentNature) => {
  const colorMap: Record<PaymentNature, string> = {
    [PaymentNature.RECEIVED]: 'success',
    [PaymentNature.RECEIVABLE]: 'warning',
    [PaymentNature.REFUND]: 'error',
  };
  return colorMap[nature] || 'default';
};

interface FinanceTableEnhancedProps {
  records: FinanceRecordWithDetails[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onUploadInvoice: (record: FinanceRecordWithDetails) => void;
  onViewEntity: (entityType: EntityType, entityId: number) => void;
  renderAttachments: (attachments: any[]) => React.ReactNode;
  currentUserRole?: string;
}

const FinanceTableEnhanced: React.FC<FinanceTableEnhancedProps> = ({
  records,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onUploadInvoice,
  onViewEntity,
  renderAttachments,
  currentUserRole,
}) => {
  const columnRenderMap: Record<string, any> = useMemo(() => ({
    index: {
      render: (_: any, __: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    contact_name: {
      render: (name: string) => name || '-',
    },
    case_name: {
      ellipsis: { showTitle: false },
      render: (name: string, record: FinanceRecordWithDetails) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Text ellipsis={{ tooltip: name }} style={{ maxWidth: '100%' }}>
            {name || '-'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ENTITY_TYPE_MAP[record.entity_type]}
          </Text>
        </Space>
      ),
    },
    expense_type: {
      render: (type: ExpenseType) => EXPENSE_TYPE_MAP[type] || type,
    },
    amount: {
      render: (amount: string | number, record: FinanceRecordWithDetails) => {
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        const color = record.payment_nature === PaymentNature.REFUND ? '#ff4d4f' : '#52c41a';
        const prefix = record.payment_nature === PaymentNature.REFUND ? '-' : '+';
        return (
          <Text strong style={{ color }}>
            {prefix}￥{numAmount.toLocaleString()}
          </Text>
        );
      },
      sorter: (a: FinanceRecordWithDetails, b: FinanceRecordWithDetails) => {
        const amountA = typeof a.amount === 'string' ? parseFloat(a.amount) : a.amount;
        const amountB = typeof b.amount === 'string' ? parseFloat(b.amount) : b.amount;
        return amountA - amountB;
      },
    },
    payment_time: {
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: FinanceRecordWithDetails, b: FinanceRecordWithDetails) => {
        if (!a.payment_time) return 1;
        if (!b.payment_time) return -1;
        return dayjs(a.payment_time).unix() - dayjs(b.payment_time).unix();
      },
    },
    payment_nature: {
      render: (nature: PaymentNature) => (
        <Tag color={getPaymentNatureColor(nature)}>
          {PAYMENT_NATURE_MAP[nature] || nature}
        </Tag>
      ),
    },
    invoices: {
      align: 'center' as const,
      render: (invoices: any[]) => renderAttachments(invoices),
    },
    attachments: {
      align: 'center' as const,
      render: (attachments: any[]) => renderAttachments(attachments),
    },
    responsible_person_name: {
      render: (name: string) => name || '-',
    },
    remark: {
      ellipsis: true,
      render: (remark: string) => remark || '-',
    },
    created_at: {
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: FinanceRecordWithDetails, b: FinanceRecordWithDetails) => {
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return dayjs(a.created_at).unix() - dayjs(b.created_at).unix();
      },
      defaultSortOrder: 'descend' as const,
    },
    action: {
      render: (_: any, record: FinanceRecordWithDetails) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            onClick={() => onUploadInvoice(record)}
          >
            上传发票
          </Button>
          {currentUserRole === 'admin' && record.entity_id && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewEntity(record.entity_type, record.entity_id)}
            >
              查看详情
            </Button>
          )}
        </Space>
      ),
    },
  }), [page, pageSize, onUploadInvoice, onViewEntity, renderAttachments, currentUserRole]);

  return (
    <EnhancedTableWrapper
      viewType="finance-management"
      defaultColumns={financeDefaultColumns}
      columnRenderMap={columnRenderMap}
      dataSource={records}
      loading={loading}
      rowKey="id"
      pagination={{
        current: page,
        pageSize: pageSize,
        total: total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        onChange: onPageChange,
      }}
      scroll={{ x: 1800, y: 600 }}
    />
  );
};

export default FinanceTableEnhanced;
